<template>
	<div class="playlist-mode mx-auto p-3 sm:p-4">
		<div class="playlist-mode-content mx-auto flex flex-col">
			<div class="playlist-hero">
				<button
					class="playlist-back-button flex flex-row hover:bg-gray-4 items-center rounded py-2 px-4 text-center font-sans mr-2 text-xs font-bold hover:text-primary-500 transition-all bg-transparent border-gray-3 border text-primary"
					type="button"
					@click="handleBackButtonClick"
				>
					<i class="tabler:arrow-left w-5 h-5 mr-1"></i>
					<span class="hidden md:inline">{{ labels.backbtn || 'Back' }}</span>
				</button>
				<div class="playlist-hero-copy">
					<h1 class="playlist-hero-title">{{ labels.podcastListTitle || 'List of podcasts' }}</h1>
					<p class="playlist-hero-subtitle">{{ songsCountLabel }}</p>
				</div>
			</div>
			<div class="Bigaudio-player">
				<BigSoundPlayer :sounds="validPlaylistMode" :darkMode="false" :draggable="false" />
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios'
import { useAppConfig } from '@/composables/useAppConfig';

import type { Callout, PostListResponse } from '@/types';
import { useAppStore } from '@/stores/app-store';
import { messages } from '@/assets/language';

const { currentLng, currentLatestDays, currentCollectionMode, currentTestyourself, currentPAGE_REQ_ID } = useAppStore()

const AppConfig = useAppConfig();

const route = useRoute();
const router = useRouter();

const props = defineProps<{
	selectedLanguage?: string
}>();

watch(
	() => props.selectedLanguage,
	(newLang) => {
		if (newLang) {
			console.log(`Gjuha e re nga prindi: ${newLang}`);
		} else {
			console.log('Gjuha nuk është kaluar ende.');
		}
	}
);

const playlistMode = ref<Callout[]>([]);
const labels = computed(() => messages[currentLng.value] ?? messages.en);
const hasPlayableAudio = (callout: Callout): callout is Callout & { file_url: string } =>
	typeof callout.file_url === 'string' && callout.file_url.trim() !== '';
const validPlaylistMode = computed(() =>
	Array.isArray(playlistMode.value) ? playlistMode.value.filter(hasPlayableAudio) : []
);
const songsCountLabel = computed(() => {
	const count = validPlaylistMode.value.length;
	return `${count} ${labels.value.podcastsInTotal || 'podcasts in total'}`;
});

const getFirstQueryString = (value: unknown): string => {
	if (Array.isArray(value)) {
		const first = value.find((entry): entry is string => typeof entry === 'string');
		return first ?? '';
	}
	return typeof value === 'string' ? value : '';
};

const getReturnToUrl = () => {
	const returnTo = getFirstQueryString(route.query.returnTo);
	if (!returnTo) {
		return '';
	}

	try {
		return decodeURIComponent(returnTo);
	} catch {
		return returnTo;
	}
};

const buildFixedListUrl = () => {
	const targetUrl = new URL(window.location.href);
	targetUrl.pathname = targetUrl.pathname.replace(/\/details\.html$/i, '/index.html');
	targetUrl.search = '';
	targetUrl.hash = '';

	const queryParams = new URLSearchParams();
	Object.entries(route.query).forEach(([key, value]) => {
		if (key === 'open' || key === 'embedded' || key === 'returnTo') {
			return;
		}
		if (typeof value === 'string') {
			queryParams.set(key, value);
			return;
		}
		if (Array.isArray(value)) {
			value.forEach((entry) => {
				if (entry != null) {
					queryParams.append(key, String(entry));
				}
			});
		}
	});

	const queryString = queryParams.toString();
	return `${targetUrl.toString()}#/${queryString ? `?${queryString}` : ''}`;
};

const handleBackButtonClick = () => {
	const returnToUrl = getReturnToUrl();
	if (returnToUrl) {
		window.location.href = returnToUrl;
		return;
	}

	window.location.href = buildFixedListUrl();
};

const fetchPlaylist = async () => {
	try {
			const requestLng = getFirstQueryString(route.query.lng) || currentLng.value;
			let params: Record<string, any> = {
				...route.query,

			};
			delete params.page;
			delete params.detailsUrl;
			delete params.detailsKey;
			delete params.currentStudyType;
			delete params.studyTpe;
			delete params.studyMode;
			delete params.open;
			delete params.embedded;
			delete params.returnTo;

		if (!params.lng && requestLng) {
			params.lng = requestLng;
		}

		if (!params['collection-mode'] && currentCollectionMode.value) {
			params['collection-mode'] = currentCollectionMode.value;
		}

		if (!params['latest-days'] && currentLatestDays.value) {
			params['latest-days'] = currentLatestDays.value
		}

		if (!params['testYourself'] && currentTestyourself.value) {
			params['testYourself'] = currentTestyourself.value
		}

		if (!params['PAGE_REQ_ID'] && currentPAGE_REQ_ID.value) {
			params['PAGE_REQ_ID'] = currentPAGE_REQ_ID.value
		}

		const response = await axios.get<PostListResponse>(`${AppConfig.urls.getList}?json=y`, {
			params: params,
			headers: {
				'Content-Type': 'application/json',
				'Accept-Language': requestLng
			}
		});

		const jsonData = response.data;
		if (Array.isArray(jsonData.data)) {
			playlistMode.value = jsonData.data;
			//console.log('playlistMode loaded:', playlistMode.value);
		} else {
			console.error('Expected an array but received:', jsonData.data);
			playlistMode.value = [];
		}
	} catch (error) {
		console.error('Failed to fetch data:', error);
	}
}

watch(
	() => currentLng.value,
	async (newLng, oldLng) => {
		if (!newLng || newLng === oldLng) {
			return
		}
		await router.replace({
			query: {
				...route.query,
				lng: String(newLng)
			}
		})
		await fetchPlaylist()
	}
)

onMounted(async () => {
	await fetchPlaylist()
});
</script>


<style>
.playlist-mode {
	--sonic-bg: #f0f3f8;
	--sonic-surface: #ffffff;
	--sonic-surface-2: #f4f7fb;
	--sonic-border: #d6deea;
	--sonic-text: #1b2a3f;
	--sonic-text-muted: #6f7e96;
	--sonic-green: #42c4b0;
	--sonic-green-dark: #2ea594;
	box-sizing: border-box;
	height: 100vh;
	height: 100dvh;
	max-height: 100dvh;
	overflow: hidden;
	overscroll-behavior: none;
	background:
		radial-gradient(640px 260px at 10% -8%, rgba(127, 179, 255, 0.2), transparent 65%),
		radial-gradient(580px 260px at 100% 0, rgba(128, 220, 209, 0.17), transparent 62%),
		linear-gradient(180deg, #f6f8fc 0%, #eef3f9 56%, #f5f8fd 100%);
	color: var(--sonic-text);
	padding: 0.75rem;
	padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 0.75rem);
}

@media (min-width: 640px) {
	.playlist-mode {
		padding: 1rem;
		padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 1rem);
	}
}

.playlist-mode-content {
	height: 100%;
	min-height: 0;
	display: flex;
	flex-direction: column;
}

.playlist-hero {
	flex-shrink: 0;
	padding: 0.2rem 0.25rem 0.85rem;
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
	border-bottom: 1px solid var(--sonic-border);
	margin-bottom: 0.85rem;
}

.playlist-back-button {
	flex-shrink: 0;
}

.playlist-hero-copy {
	min-width: 0;
	text-align: right;
}

.playlist-hero-title {
	font-size: clamp(1.55rem, 2.7vw, 2.05rem);
	font-weight: 750;
	letter-spacing: -0.01em;
	color: var(--sonic-text);
	line-height: 1.12;
}

.playlist-hero-subtitle {
	margin-top: 0.28rem;
	font-size: 0.96rem;
	font-weight: 550;
	color: var(--sonic-text-muted);
}

.Bigaudio-player {
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.Bigaudio-player .soundPlayer {
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	align-items: stretch !important;
	gap: 0.75rem;
	transform: none !important;
	width: 100% !important;
	max-width: none;
	position: static !important;
	left: auto !important;
	right: auto !important;
	top: auto !important;
	bottom: auto !important;
	background: transparent;
	padding: 0 !important;
	box-shadow: none;
	margin: 0 auto;
}

.Bigaudio-player .soundPlayer-content {
	flex-shrink: 0;
	display: flex;
	align-items: stretch;
	background:
		linear-gradient(180deg, rgba(255, 255, 255, 0.97) 0%, rgba(246, 250, 255, 0.95) 100%);
	border: 1px solid var(--sonic-border);
	border-radius: 22px;
	padding: 1rem;
	box-shadow:
		0 14px 30px rgba(16, 24, 40, 0.11),
		0 1px 0 rgba(255, 255, 255, 0.9) inset;
}

.Bigaudio-player .soundPlayer-content > .flex-1.w-100% {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	min-width: 0;
}

.Bigaudio-player .soundPlayer-content .flex.sm\:flex-row.flex-col.items-center.flex-1.mb-2.mx-3 {
	margin: 0 !important;
	padding: 0 0.2rem;
	align-items: flex-start;
}

.Bigaudio-player .soundPlayer-content .flex.items-center.flex-1.w-100\%.sm\:flex-row.flex-col {
	display: flex;
	flex-direction: column !important;
	align-items: stretch !important;
	gap: 0.75rem;
}

.Bigaudio-player .soundPlayer-content .flex.flex-1.w-100\%.items-center.justify-between {
	background: linear-gradient(180deg, #fdfefe 0%, #f5faf9 100%);
	border: 1px solid #d4e7e2;
	border-radius: 14px;
	padding: 0.55rem 0.65rem;
	gap: 0.5rem;
}

.Bigaudio-player .soundPlayer-timer {
	font-variant-numeric: tabular-nums;
	color: var(--sonic-text-muted);
	font-weight: 700;
	letter-spacing: 0.02em;
	font-size: 1.02rem;
}

.Bigaudio-player .soundPlayer-slider {
	width: 100% !important;
	height: 7px;
	border-radius: 999px;
	background: linear-gradient(
		to right,
		var(--sonic-green) var(--slider-progress, 0%),
		#d0d8e5 var(--slider-progress, 0%)
	) !important;
}

.Bigaudio-player .soundPlayer-slider::-webkit-slider-thumb {
	width: 17px;
	height: 17px;
	border-radius: 999px;
	background: #fff;
	border: 2px solid #2fa694;
	box-shadow: 0 0 0 4px rgba(66, 196, 176, 0.18);
}

.Bigaudio-player .soundPlayer-controls .soundPlayer-actions-btn {
	width: 64px;
	height: 64px;
	border-radius: 50%;
	background: linear-gradient(180deg, #4ac7b2 0%, #2ea593 100%) !important;
	border: 0 !important;
	color: #fff !important;
	box-shadow:
		0 12px 22px rgba(46, 165, 147, 0.34),
		0 2px 0 rgba(255, 255, 255, 0.22) inset;
}

.Bigaudio-player .soundPlayer-controls .soundPlayer-actions-btn svg {
	fill: #fff !important;
}

.Bigaudio-player .soundPlayer-content .soundPlayer-actions-btn {
	border-radius: 12px !important;
	border: 1px solid #d8e2f1 !important;
	background: var(--sonic-surface-2) !important;
	color: #2f506d !important;
	transition: transform 0.18s ease, background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
	box-shadow: 0 1px 0 rgba(255, 255, 255, 0.85) inset;
	min-width: 46px;
	min-height: 42px;
}

.Bigaudio-player .soundPlayer-content .soundPlayer-actions-btn svg {
	fill: currentColor !important;
}

.Bigaudio-player .soundPlayer-content .soundPlayer-actions-btn:hover {
	transform: translateY(-1px);
	background: #ecf8f5 !important;
	border-color: #bfe2da !important;
	box-shadow: 0 6px 12px rgba(32, 50, 74, 0.12);
}

.Bigaudio-player .soundPlayer-content .soundPlayer-controls .soundPlayer-actions-btn {
	width: 64px;
	height: 64px;
	border-radius: 50% !important;
	background: linear-gradient(180deg, #4ac7b2 0%, #2ea593 100%) !important;
	border: 0 !important;
	transform: none;
}

.Bigaudio-player .soundPlayer-content .soundPlayer-controls .soundPlayer-actions-btn:hover {
	background: linear-gradient(180deg, #54d3be 0%, #34ad9b 100%) !important;
	border-color: transparent !important;
	transform: none;
}

.Bigaudio-player .soundPlayer-content .soundPlayer-controls .soundPlayer-actions-btn svg {
	fill: #fff !important;
}

.Bigaudio-player .soundPlayer-content [class*='md:flex-row'][class*='gap-2'] {
	display: flex;
	width: 100%;
	padding: 0.1rem 0;
	gap: 0.48rem !important;
	flex-wrap: wrap;
	justify-content: center;
}

.Bigaudio-player .soundPlayer-volume-box {
	background: #ffffff;
	border: 1px solid #d8e2f1;
	box-shadow: 0 14px 28px rgba(16, 24, 40, 0.16);
}

.Bigaudio-player .soundPlayer-content .soundPlayer-volume {
	width: 40px;
	height: 40px;
	border-radius: 10px !important;
}

.Bigaudio-player .soundPlayer-soundList {
	flex: 1;
	min-height: 0;
	display: flex !important;
	flex-direction: column;
	width: 100% !important;
	max-width: 100% !important;
	align-self: stretch !important;
	overflow: hidden;
	position: relative !important;
	right: 0 !important;
	bottom: 0 !important;
	top: 0 !important;
	margin: 0 auto !important;
	background: transparent;
	border: none;
	border-radius: 0;
	padding: 0;
	box-shadow: none;
}

.Bigaudio-player .soundPlayer-soundList-content {
	flex: 1;
	min-height: 0;
	height: 100%;
	width: 100% !important;
	max-height: none !important;
	overflow-y: auto !important;
	overflow-x: hidden;
	-webkit-overflow-scrolling: touch;
	overscroll-behavior: contain;
	background: rgba(248, 251, 255, 0.92);
	border: 1px solid var(--sonic-border);
	border-radius: 14px;
	padding: 0.34rem 0.38rem;
	scrollbar-width: thin;
	scrollbar-color: #8ea3c6 #e8eef8;
}

.Bigaudio-player .soundPlayer-soundList-content::-webkit-scrollbar {
	width: 8px;
}

.Bigaudio-player .soundPlayer-soundList-content::-webkit-scrollbar-track {
	background: #e8eef8;
	border-radius: 999px;
}

.Bigaudio-player .soundPlayer-soundList-content::-webkit-scrollbar-thumb {
	background: #8ea3c6;
	border-radius: 999px;
}

.Bigaudio-player .soundPlayer-soundList button {
	margin: 0 !important;
	padding-left: 0;
	padding-right: 0;
}

.Bigaudio-player .soundPlayer-soundList-item {
	display: grid;
	grid-template-columns: 56px minmax(0, 1fr) auto;
	align-items: center;
	column-gap: 0.7rem;
	width: 100%;
	margin-bottom: 0.28rem;
	border: 1px solid #deebf8;
	background: linear-gradient(180deg, #ffffff 0%, #f9fbff 100%);
	border-radius: 10px;
	padding: 0.52rem 0.58rem !important;
	transition: background-color 0.22s ease, box-shadow 0.22s ease, transform 0.22s ease, border-color 0.22s ease;
}

.Bigaudio-player .soundPlayer-soundList-item:nth-child(n + 2) {
	border-color: #deebf8;
}

.Bigaudio-player .soundPlayer-soundList-item:hover {
	background: #f1f8ff;
	transform: translateY(-1px);
}

.Bigaudio-player .soundPlayer-soundList-item.active {
	background: linear-gradient(180deg, #effcf9 0%, #e4f8f2 100%);
	border-color: #8fd6c9;
	box-shadow: 0 7px 14px rgba(46, 165, 147, 0.15);
}

.Bigaudio-player .soundPlayer-soundList-item.is-current {
	border-color: #9dded2;
	box-shadow:
		0 8px 16px rgba(46, 165, 147, 0.12),
		inset 0 0 0 1px rgba(46, 165, 147, 0.08);
}

.Bigaudio-player .soundPlayer-soundList-item.is-opening {
	animation: podcastOpenFx 560ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.Bigaudio-player .soundPlayer-content > div:first-child img {
	animation: coverPopFx 440ms cubic-bezier(0.21, 0.8, 0.25, 1);
}

@keyframes podcastOpenFx {
	0% {
		transform: scale(0.975) translateY(6px);
		opacity: 0.72;
	}
	60% {
		transform: scale(1.01) translateY(-1px);
	}
	100% {
		transform: scale(1) translateY(0);
		opacity: 1;
	}
}

@keyframes coverPopFx {
	0% {
		transform: scale(0.94) translateY(6px);
		opacity: 0.6;
		filter: saturate(0.8);
	}
	100% {
		transform: scale(1) translateY(0);
		opacity: 1;
		filter: saturate(1);
	}
}

.Bigaudio-player .soundPlayer-soundList-item > .soundPlayer-icon-wrapper {
	display: none;
}

.Bigaudio-player .soundPlayer-soundList-item > div:nth-child(2) {
	display: flex !important;
	width: 56px !important;
	min-width: 56px !important;
	height: 56px;
	margin: 0 !important;
	padding: 0 !important;
}

.Bigaudio-player .soundPlayer-soundList-item > div:nth-child(2) img {
	display: block;
	width: 56px !important;
	height: 56px !important;
	max-width: none !important;
	max-height: none !important;
	object-fit: cover;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(17, 24, 39, 0.12);
}

.Bigaudio-player .soundPlayer-soundList-item > div:nth-child(3) {
	margin: 0 !important;
	min-width: 0;
	display: flex;
	flex-direction: column;
}

.Bigaudio-player .soundPlayer-soundList-item > div:nth-child(3) h2 {
	order: 1;
	margin: 0;
	font-size: 1rem !important;
	font-weight: 700;
	line-height: 1.2;
	color: #1f2a44;
	direction: ltr;
	text-align: left;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.Bigaudio-player .soundPlayer-soundList-item > div:nth-child(3) p {
	order: 2;
	margin: 0.16rem 0 0 !important;
	font-size: 0.87rem;
	font-weight: 500;
	color: #677691;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.Bigaudio-player .soundPlayer-soundList-item > div:nth-child(3) small {
	order: 3;
	margin-top: 0.22rem;
	font-size: 0.78rem !important;
	font-weight: 600;
	color: #25a36f !important;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.Bigaudio-player .soundPlayer-soundList-item > div:nth-child(3) small:first-of-type {
	display: none;
}

.Bigaudio-player .soundPlayer-soundList-item > .soundPlayer-timer {
	margin-left: 0 !important;
	font-size: 0.86rem;
	font-weight: 700;
	color: #5e6f8f;
}

@media (max-width: 767px) {
	.playlist-hero {
		padding: 0.05rem 0.1rem 0.7rem;
		gap: 0.65rem;
	}

	.playlist-hero-title {
		font-size: 1.42rem;
	}

	.playlist-hero-subtitle {
		font-size: 0.9rem;
	}

	.Bigaudio-player .soundPlayer-content {
		padding: 0.72rem;
		border-radius: 16px;
	}

	.Bigaudio-player .soundPlayer-controls .soundPlayer-actions-btn {
		width: 56px;
		height: 56px;
	}

	.Bigaudio-player .soundPlayer-content .soundPlayer-actions-btn {
		min-height: 40px;
		min-width: 42px;
	}

	.Bigaudio-player .soundPlayer-content .flex.flex-1.w-100\%.items-center.justify-between {
		padding: 0.45rem 0.5rem;
	}
}
</style>
